import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  PenTool, FileText, Image, Layers, Ruler, GitCommit, Type,
  ChevronDown, ChevronUp, ArrowLeft, Lightbulb
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

const ICON_MAP = {
  'pen-tool': PenTool,
  file: FileText,
  image: Image,
  layers: Layers,
  ruler: Ruler,
  'git-commit': GitCommit,
  type: Type,
};

const LaserDesignPrep = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/laser/design-prep`)
      .then(res => {
        setEntries(res.data || []);
        if (res.data?.length > 0) setExpanded({ [res.data[0].id]: true });
      })
      .catch(err => console.error('Failed to load design prep guide:', err))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.LASER_DESIGN_PREP)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <PenTool className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading design prep guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="laser-design-prep" style={bgStyle(IMAGES.LASER_DESIGN_PREP)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <Link to="/laser" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <PenTool className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">FILE PREPARATION</div>
            <h1 className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Design File Prep
            </h1>
            <p className="text-sm text-zinc-300 mt-1">
              SVG · DXF · LightBurn colour mapping · Kerf compensation · Text to paths
            </p>
          </div>
        </div>
      </div>

      {/* Quick tip banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-orange-900/50 border border-orange-500/40 mb-6">
        <Lightbulb className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
        <p className="text-orange-200 text-sm">
          Poor file preparation is responsible for most laser cutting failures. Taking 5 minutes to
          prepare a clean file correctly saves hours of wasted material and troubleshooting.
        </p>
      </div>

      <div className="space-y-4 max-w-4xl">
        {entries.map(entry => {
          const Icon = ICON_MAP[entry.icon] || PenTool;
          const isOpen = expanded[entry.id];

          return (
            <Card key={entry.id} className="bg-zinc-900 border-zinc-800">
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => toggle(entry.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-zinc-800">
                      <Icon className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{entry.title}</CardTitle>
                      <CardDescription className="text-zinc-400 text-sm mt-0.5">{entry.summary}</CardDescription>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                           : <ChevronDown className="w-5 h-5 text-zinc-400 flex-shrink-0" />}
                </div>
              </CardHeader>

              {isOpen && (
                <CardContent className="space-y-5 pt-0">
                  {entry.points?.map((point, i) => (
                    <div key={i} className="border-l-2 border-orange-500/30 pl-4">
                      <h4 className="text-white font-semibold text-sm mb-1">{point.heading}</h4>
                      <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{point.body}</p>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}

        {entries.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            <PenTool className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No design prep entries found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LaserDesignPrep;
